import React from "react";
import {
  useTable,
  usePagination,
  useFilters,
  useSortBy,
  useBlockLayout,
} from "react-table";
import { useSticky } from 'react-table-sticky';
import { useExportData } from "react-table-plugins";
import Papa from "papaparse";
import XLSX from "xlsx";
import JsPDF from "jspdf";
import "jspdf-autotable";
import "../css/tablecomandas.css";

import { GlobalFilter, DefaultFilterForColumn } from "./Filter";

function getExportFileBlob({ columns, data, fileType }) {
  if (fileType === "csv") {
    const headerNames = columns.map((col) => col.exportValue);
    const csvString = Papa.unparse({ fields: headerNames, data });
    return new Blob([csvString], { type: "text/csv" });
  }
  if (fileType === "pdf") {
    const headerNames = columns.map((column) => column.exportValue);
    const doc = new JsPDF();
    const fecha = new Date().toLocaleDateString();
    doc.text(`INFORME ADMINISTRACION DE COMANDAS - Fecha: ${fecha}`, 10, 10);
    doc.text(`Total Registros: ${data.length}`, 10, 16);

    const docBody = data.map((row) => Object.values(row));
    doc.autoTable({
      head: [headerNames],
      body: docBody,
      margin: { top: 25 },
      styles: { theme: 'grid', minCellHeight: 22, halign: "center", valign: "center", fontSize: 7 },
    });
    doc.save(`Informe_comandas_${fecha}.pdf`);
    return false;
  }
  return false;
}

export default function Table({ columns, data }) {
  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    footerGroups,
    page,
    prepareRow,
    state: { pageIndex, pageSize },
    gotoPage,
    previousPage,
    nextPage,
    canPreviousPage,
    canNextPage,
    pageOptions,
    pageCount,
    setPageSize,
    exportData,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageSize: 50, pageIndex: 0 },
      defaultColumn: { Filter: DefaultFilterForColumn },
      getExportFileBlob,
      filterTypes: {
        equals: (rows, id, filterValue) =>
          rows.filter((row) => row.values[id] === filterValue),
      },
    },
    useFilters,
    useSortBy,
    usePagination,
    useExportData,
    useSticky,
    useBlockLayout
  );

  return (
    <>
      {/* Export buttons */}
      <div className="container mb-3">
        <div className="d-flex">
          <button
            className="btn btn-primary btn-sm flex-fill mx-1"
            style={{ fontSize: '1rem' }}
            onClick={() => exportData("csv", true)}
          >
            Exportar TODO CSV/Excel
          </button>
          <button
            className="btn btn-info btn-sm flex-fill mx-1"
            style={{ fontSize: '1rem' }}
            onClick={() => exportData("csv", false)}
          >
            Exportar Vista CSV/Excel
          </button>
          <button
            className="btn btn-success btn-sm flex-fill mx-1"
            style={{ fontSize: '1rem' }}
            onClick={() => exportData("pdf", true)}
          >
            Exportar TODO PDF
          </button>
          <button
            className="btn btn-warning btn-sm flex-fill mx-1"
            style={{ fontSize: '1rem' }}
            onClick={() => exportData("pdf", false)}
          >
            Exportar Vista PDF
          </button>
        </div>
      </div>

      {/* Data table */}
      <table className="table sticky table-striped table-bordered" {...getTableProps()}>
        <thead>
          {headerGroups.map((headerGroup) => (
            <tr {...headerGroup.getHeaderGroupProps()}>
              {headerGroup.headers.map((column) => (
                <th
                  {...column.getHeaderProps(column.getSortByToggleProps())}
                  className={column.className}
                >
                  {column.render('Header')}
                  <span>
                    {column.isSorted ? (column.isSortedDesc ? ' 🔽' : ' 🔼') : ''}
                  </span>
                  <div>{column.canFilter ? column.render('Filter') : null}</div>
                </th>
              ))}
            </tr>
          ))}
        </thead>
        <tbody {...getTableBodyProps()}>
          {page.map((row) => {
            prepareRow(row);
            return (
              <tr {...row.getRowProps()}>
                {row.cells.map((cell) => (
                  <td
                    {...cell.getCellProps({ className: cell.column.className })}
                  >
                    {cell.render('Cell')}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
        <tfoot>
          {footerGroups.map((group) => (
            <tr {...group.getFooterGroupProps()}>
              {group.headers.map((column) => (
                <td {...column.getFooterProps()}>{column.render('Footer')}</td>
              ))}
            </tr>
          ))}
        </tfoot>
      </table>

      {/* Pagination */}
      <div className="pagination">
        <button onClick={() => gotoPage(0)} disabled={!canPreviousPage}>{'<<'}</button>{' '}
        <button onClick={() => previousPage()} disabled={!canPreviousPage}>{'<'}</button>{' '}
        <button onClick={() => nextPage()} disabled={!canNextPage}>{'>'}</button>{' '}
        <button onClick={() => gotoPage(pageCount - 1)} disabled={!canNextPage}>{'>>'}</button>{' '}
        <span>
          Página {pageIndex + 1} de {pageOptions.length} {' - Ir a:'}
          <input
            type="number"
            defaultValue={pageIndex + 1}
            onChange={(e) => gotoPage(e.target.value ? Number(e.target.value) - 1 : 0)}
            style={{ width: '60px', marginLeft: '4px' }}
          />
        </span>{' '}
        <select
          value={pageSize}
          onChange={(e) => setPageSize(Number(e.target.value))}
          style={{ marginLeft: '8px' }}
        >
          {[50, 100, 150, 200, 250].map((size) => (
            <option key={size} value={size}>
              Mostrar {size}
            </option>
          ))}
        </select>
      </div>
    </>
  );
}
